import type {
  AssistantInsightsFilters,
  AssistantUsageTopic,
} from '@/api/assistantInsights/assistantInsightsApi.types';

export type UsagePeriod =
  'this_month' | 'last_month' | 'last_90_days' | 'last_12_months' | 'custom';

export const USAGE_PERIODS: UsagePeriod[] = [
  'this_month',
  'last_month',
  'last_90_days',
  'last_12_months',
  'custom',
];

export const USAGE_TOPICS: AssistantUsageTopic[] = [
  'help',
  'workspace_setup',
  'integrations',
  'schedule',
  'patient_records',
  'clinical_notes',
  'daily_brief',
  'general',
];

export const USAGE_FILTER_PARAMETERS = {
  period: 'period',
  from: 'from',
  to: 'to',
  workspace: 'workspace',
  workspaceName: 'workspaceName',
  topic: 'topic',
  screen: 'screen',
} as const;

const DAYS_IN_RECENT_QUARTER = 90;

export interface UsageFilterState {
  period: UsagePeriod;
  customFrom: string;
  customTo: string;
  tenantId: string | null;
  tenantName: string | null;
  topic: AssistantUsageTopic | null;
  screen: string | null;
}

function toIsoDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

function utcDate(year: number, monthIndex: number, day: number): Date {
  return new Date(Date.UTC(year, monthIndex, day));
}

function shiftDays(value: Date, days: number): Date {
  return new Date(value.getTime() + days * 86_400_000);
}

export function resolvePeriodRange(
  period: UsagePeriod,
  today: Date,
  customFrom: string,
  customTo: string
): { dateFrom: string; dateTo: string } {
  const year = today.getUTCFullYear();
  const monthIndex = today.getUTCMonth();
  const todayIso = toIsoDate(today);
  if (period === 'last_month') {
    return {
      dateFrom: toIsoDate(utcDate(year, monthIndex - 1, 1)),
      dateTo: toIsoDate(utcDate(year, monthIndex, 0)),
    };
  }
  if (period === 'last_90_days') {
    return { dateFrom: toIsoDate(shiftDays(today, 1 - DAYS_IN_RECENT_QUARTER)), dateTo: todayIso };
  }
  if (period === 'last_12_months') {
    return {
      dateFrom: toIsoDate(shiftDays(utcDate(year - 1, monthIndex, today.getUTCDate()), 1)),
      dateTo: todayIso,
    };
  }
  if (period === 'custom' && customFrom.length > 0 && customTo.length > 0) {
    return { dateFrom: customFrom, dateTo: customTo };
  }
  return { dateFrom: toIsoDate(utcDate(year, monthIndex, 1)), dateTo: todayIso };
}

function isUsagePeriod(value: string | null): value is UsagePeriod {
  return value !== null && (USAGE_PERIODS as string[]).includes(value);
}

function isUsageTopic(value: string | null): value is AssistantUsageTopic {
  return value !== null && (USAGE_TOPICS as string[]).includes(value);
}

export function readUsageFilterState(searchParameters: URLSearchParams): UsageFilterState {
  const period = searchParameters.get(USAGE_FILTER_PARAMETERS.period);
  const topic = searchParameters.get(USAGE_FILTER_PARAMETERS.topic);
  return {
    period: isUsagePeriod(period) ? period : 'this_month',
    customFrom: searchParameters.get(USAGE_FILTER_PARAMETERS.from) || '',
    customTo: searchParameters.get(USAGE_FILTER_PARAMETERS.to) || '',
    tenantId: searchParameters.get(USAGE_FILTER_PARAMETERS.workspace),
    tenantName: searchParameters.get(USAGE_FILTER_PARAMETERS.workspaceName),
    topic: isUsageTopic(topic) ? topic : null,
    screen: searchParameters.get(USAGE_FILTER_PARAMETERS.screen),
  };
}

export function writeUsageFilterState(
  searchParameters: URLSearchParams,
  state: UsageFilterState
): URLSearchParams {
  const next = new URLSearchParams(searchParameters);
  const entries: [string, string | null][] = [
    [USAGE_FILTER_PARAMETERS.period, state.period === 'this_month' ? null : state.period],
    [USAGE_FILTER_PARAMETERS.from, state.period === 'custom' ? state.customFrom : null],
    [USAGE_FILTER_PARAMETERS.to, state.period === 'custom' ? state.customTo : null],
    [USAGE_FILTER_PARAMETERS.workspace, state.tenantId],
    [USAGE_FILTER_PARAMETERS.workspaceName, state.tenantName],
    [USAGE_FILTER_PARAMETERS.topic, state.topic],
    [USAGE_FILTER_PARAMETERS.screen, state.screen],
  ];
  entries.forEach(function applyEntry([name, value]) {
    if (value === null || value.length === 0) {
      next.delete(name);
    } else {
      next.set(name, value);
    }
  });
  return next;
}

export function toInsightsFilters(state: UsageFilterState, today: Date): AssistantInsightsFilters {
  const range = resolvePeriodRange(state.period, today, state.customFrom, state.customTo);
  return {
    dateFrom: range.dateFrom,
    dateTo: range.dateTo,
    tenantId: state.tenantId,
    topic: state.topic,
    screen: state.screen,
  };
}
